import { FC, Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useDropzone, FileRejection } from "react-dropzone";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Transition,
  Dialog,
  TransitionChild,
  DialogPanel,
  DialogTitle,
  Checkbox,
} from "@headlessui/react";
import { useData } from "data/data-provider";
import {
  cancelBooking,
  retriveBooking,
  getTripPhotos,
} from "api/booking/booking";
import { getCustomerFiles, uploadCustomerFiles } from "api/customer/customer";
import toast from "react-hot-toast";
import { globalJson } from "global/global_json";
import {
  getPrivacyPolicyContent,
  sendRentalAgreement,
} from "api/rental-agreement/rental-agreement";
import {
  CheckIcon,
  ExclamationTriangleIcon,
  CameraIcon,
} from "@heroicons/react/24/outline";
import { Squares2X2Icon, XMarkIcon } from "@heroicons/react/24/solid";
import { formatPrice } from "utils/convertNumbThousand";
import Cancellation from "containers/PageHome/Cancellation";
import Invoice from "designs/Common/(car-search-form)/invoice";
import moment from "moment";
import {
  CreateStripeVerification,
  CreateVouchVerification,
} from "api/stripe/stripe";
import { CreateCanopyVerification } from "api/Canopy/canopy";
import { CanopyPayload } from "api/types";
import Badge from "shared/Badge/Badge";
import { getBonzahPDF, getBonzahProducts } from "api/bonzah/bonzah";
import {
  formatDateToHumanFirendly,
  formatTimeToHumanFirendly12Hour,
  formatTimeToHumanFirendly24Hour,
} from "utils/date-format";
import { Slide } from "react-slideshow-image";
import "react-slideshow-image/dist/styles.css";
import PhoneNumberDisplay from "utils/PhoneFormat";
import EditBooking from "components/EditBooking/EditBooking";
import PreviousBookings from "components/PreviousBookings/PreviousBooking";
import RenterInfo from "components/EditBooking/RenterInfo";
import ConnectedBookings from "designs/Common/ConnectedBookings/ConnectedBookings";
import TripInspectionModal from "designs/Common/BookingDetailsPage/TripInspectionModal";
import { readOdometer, OdometerReadResult } from "api/vehicles/vehicle";
import { getAgreementPdfBase } from "utils/agreement-url";
import { isUsableUrl } from "utils/isUsableUrl";

export interface bookingDetailPageProps {
  className?: string;
}

const VIDEO_EXTS = [
  ".mp4",
  ".mkv",
  ".wmv",
  ".mov",
  ".webm",
  ".avi",
  ".flv",
  ".m4v",
];

function classifyTripPhotos(photos: any[], type: string) {
  let images = 0;
  let videos = 0;
  for (const p of photos) {
    if (p?.image_type !== type) continue;
    const name = String(p?.photo_name || p?.image || "").toLowerCase();
    if (VIDEO_EXTS.some((ext) => name.endsWith(ext))) videos += 1;
    else images += 1;
  }
  return { images, videos };
}

function summarizeFileMix(b: { images: number; videos: number }) {
  if (!b.images && !b.videos) return "No files yet";
  const imgPart = b.images === 1 ? "1 image" : `${b.images} images`;
  const vidPart = b.videos === 1 ? "1 video" : `${b.videos} videos`;
  if (b.videos === 0) return imgPart;
  if (b.images === 0) return vidPart;
  return `${imgPart}, ${vidPart}`;
}

/**
 * A fleet photo's `photo_url` can be missing, null, or the literal string
 * "undefined" -- unguarded `.includes("http")` calls on it throw, and an
 * unguarded template literal builds `.../undefined` (ONENOW-457, 1NOW-3011).
 */
const fleetPhotoSrc = (url?: string | null): string =>
  isUsableUrl(url)
    ? url.includes("http")
      ? url
      : `https://fleet-management-images-upload-be.s3.amazonaws.com/${url}`
    : "https://tenant.fleetwire.io/images/vehicle-placeholder.jpg";

const BookingDetailPage: FC<bookingDetailPageProps> = ({ className = "" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    ourCompany,
    setPickupLocation,
    setSelectedExtras,
    setSelectedBonzahProducts,
    startHours,
    startMinutes,
    endHours,
    endMinutes,
    setSearchedDropoffLocation,
    setSearchedPickupLocation,
    setSelectedCar,
    setDropoffLocation,
    setEndDate,
    setStartDate,
    setDropoffLocationType,
    setStartTime,
    startTime,
    setEndTime,
    endTime,
  } = useData();

  const queryParams = new URLSearchParams(location.search);
  let bookingId = queryParams.get("id");

  const [bookingloading, setBookingLoading] = useState(true);
  const [isTotalBillModalOpen, setIsTotalBillModalOpen] = useState(false);
  let [isOpen, setIsOpen] = useState(false);
  let [bookingDetails, setBookingDetail] = useState<any | null>(null);
  const [canopyLoading, setCanopyLoading] = useState(false);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [drivercanopyLoading, setDriverCanopyLoading] = useState(false);
  const [driveridentityLoading, setDriverIdentityLoading] = useState(false);
  const [canopyLink, setCanopyLink] = useState<any>();
  const [identityLink, setIdentityLink] = useState<any>();
  const [drivercanopyLink, setDriverCanopyLink] = useState<any>();
  const [driveridentityLink, setDriverIdentityLink] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [isRenterInfoEdit, setIsRenterInfoEdit] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  /* 1NOW-10707 — every request that names a booking or a renter sends the
     uuid, never the sequential pk. These endpoints take no login, so a
     guessable integer let anyone read a stranger's booking or start
     verification against their identity. Both uuids are already here: the page
     was opened with the booking's, and the API returns the customer's. */
  const bookingUid: string | undefined =
    bookingDetails?.unique_identifier ?? bookingId ?? undefined;
  const customerUid: string | undefined =
    bookingDetails?.customer?.unique_identifier;
  const secondaryDriverUid: string | undefined =
    bookingDetails?.secondary_driver_obj?.unique_identifier;
  // Trip inspection modal (photos / odometer / claim) state.
  const [isInspectionOpen, setIsInspectionOpen] = useState(false);
  const [inspectionPhase, setInspectionPhase] = useState<"pre" | "post">("pre");
  const [preOdometerImage, setPreOdometerImage] = useState<string | null>(null);
  const [postOdometerImage, setPostOdometerImage] = useState<string | null>(
    null
  );
  const [preOdometerLoading, setPreOdometerLoading] = useState(false);
  const [postOdometerLoading, setPostOdometerLoading] = useState(false);
  const [preOdometerResult, setPreOdometerResult] =
    useState<OdometerReadResult | null>(null);
  const [postOdometerResult, setPostOdometerResult] =
    useState<OdometerReadResult | null>(null);
  const [preOdometerError, setPreOdometerError] = useState<string | null>(null);
  const [postOdometerError, setPostOdometerError] = useState<string | null>(
    null
  );
  // Pre/post trip photo file-mix summaries shown on the inspection cards.
  const [preTripBreakdown, setPreTripBreakdown] = useState({
    images: 0,
    videos: 0,
  });
  const [postTripBreakdown, setPostTripBreakdown] = useState({
    images: 0,
    videos: 0,
  });

  // Cancellation policy display state.
  const [cancellationPolicy, setCancellationPolicy] = useState<any>();
  const [isCancellationOpen, setIsCancellationOpen] = useState(false);

  // Cancel-booking flow state (modal + policy acceptance + in-flight guard).
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancellationPolicyChecked, setCancellationPolicyChecked] =
    useState(false);

  // Customer document upload state (insurance + ID).
  const [insuranceDocuments, setInsuranceDocuments] = useState<any[]>([]);
  const [pendingInsuranceFiles, setPendingInsuranceFiles] = useState<File[]>(
    []
  );
  const [insuranceUploading, setInsuranceUploading] = useState(false);
  const [insuranceUploadProgress, setInsuranceUploadProgress] = useState(0);

  const [idDocuments, setIdDocuments] = useState<any[]>([]);
  const [pendingIdFiles, setPendingIdFiles] = useState<File[]>([]);
  const [idUploading, setIdUploading] = useState(false);
  const [idUploadProgress, setIdUploadProgress] = useState(0);

  // Cancellation-policy auto-popup (opened once per booking via localStorage).
  const [isCancellationPolicyModalOpen, setIsCancellationPolicyModalOpen] =
    useState(false);

  // "Show all photos" grid modal (gated on >1 photo).
  const [isGridOpen, setIsGridOpen] = useState(false);

  // Popup-blocked fallbacks — surface Copy/Open rows when window.open is blocked.
  const [showCopyIdentity, setShowCopyIdentity] = useState(false);
  const [showCopyCanopy, setShowCopyCanopy] = useState(false);
  const [showCopyDriverIdentity, setShowCopyDriverIdentity] = useState(false);
  const [showCopyDriverCanopy, setShowCopyDriverCanopy] = useState(false);

  // Stable toast id so duplicate copy toasts collapse into one (ONENOW-466).
  const COPY_TOAST_ID = "copy_link";

  const onDropInsurance = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setPendingInsuranceFiles((prev) => [...prev, ...acceptedFiles]);
      // 1NOW-1232: proof of insurance is a document — a dropped video is
      // silently rejected by `accept` above, so say why instead of nothing happening.
      if (fileRejections.length > 0) {
        toast.error("Videos aren't supported. Please upload an image or PDF.");
      }
    },
    [],
  );

  const {
    getRootProps: getInsuranceRootProps,
    getInputProps: getInsuranceInputProps,
    isDragActive: isInsuranceDragActive,
  } = useDropzone({
    onDrop: onDropInsurance,
    accept: { "image/*": [], "application/pdf": [] },
    multiple: true,
  });

  const onDropId = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setPendingIdFiles((prev) => [...prev, ...acceptedFiles]);
      // 1NOW-1232: same document-only gate for the ID upload.
      if (fileRejections.length > 0) {
        toast.error("Videos aren't supported. Please upload an image or PDF.");
      }
    },
    [],
  );

  const {
    getRootProps: getIdRootProps,
    getInputProps: getIdInputProps,
    isDragActive: isIdDragActive,
  } = useDropzone({
    onDrop: onDropId,
    accept: { "image/*": [], "application/pdf": [] },
    multiple: true,
  });

  const fetchInsuranceDocuments = async (customerId: number | string) => {
    const result = await getCustomerFiles(customerId, "insurance");
    if (result.data) {
      setInsuranceDocuments(
        Array.isArray(result.data) ? result.data : result.data.results ?? []
      );
    }
  };

  const fetchIdDocuments = async (customerId: number | string) => {
    const result = await getCustomerFiles(customerId, "identity");
    if (result.data) {
      setIdDocuments(
        Array.isArray(result.data) ? result.data : result.data.results ?? []
      );
    }
  };

  const handleInsuranceUpload = async () => {
    if (!customerUid || pendingInsuranceFiles.length === 0)
      return;
    setInsuranceUploading(true);
    setInsuranceUploadProgress(0);
    try {
      const result = await uploadCustomerFiles(
        customerUid,
        pendingInsuranceFiles,
        "insurance",
        (percent) => setInsuranceUploadProgress(percent)
      );
      if (result.error) {
        toast.error(result.error);
        return;
      }
      setInsuranceUploadProgress(100);
      setPendingInsuranceFiles([]);
      toast.success("Documents uploaded successfully");
      fetchInsuranceDocuments(customerUid);
    } catch {
      toast.error("Upload failed");
    } finally {
      setInsuranceUploading(false);
      setInsuranceUploadProgress(0);
    }
  };

  const handleIdUpload = async () => {
    if (!customerUid || pendingIdFiles.length === 0) return;
    setIdUploading(true);
    setIdUploadProgress(0);
    try {
      const result = await uploadCustomerFiles(
        customerUid,
        pendingIdFiles,
        "identity",
        (percent) => setIdUploadProgress(percent)
      );
      if (result.error) {
        toast.error(result.error);
        return;
      }
      setIdUploadProgress(100);
      setPendingIdFiles([]);
      toast.success("ID documents uploaded successfully");
      fetchIdDocuments(customerUid);
    } catch {
      toast.error("Upload failed");
    } finally {
      setIdUploading(false);
      setIdUploadProgress(0);
    }
  };

  const handleUploadOdometer = async (file: File, isPre: boolean) => {
    if (!bookingUid) return;
    const localUrl = URL.createObjectURL(file);
    if (isPre) {
      setPreOdometerImage(localUrl);
      setPreOdometerLoading(true);
      setPreOdometerError(null);
    } else {
      setPostOdometerImage(localUrl);
      setPostOdometerLoading(true);
      setPostOdometerError(null);
    }
    try {
      const { data, error } = await readOdometer(
        file,
        bookingUid,
        isPre ? "pre_odometer" : "post_odometer"
      );
      if (error || !data) {
        throw new Error(error || "Could not read odometer.");
      }
      if (isPre) {
        setPreOdometerResult(data);
      } else {
        setPostOdometerResult(data);
      }
    } catch (err: any) {
      if (isPre) {
        setPreOdometerError(err?.message || "Could not read odometer.");
      } else {
        setPostOdometerError(err?.message || "Could not read odometer.");
      }
    } finally {
      if (isPre) {
        setPreOdometerLoading(false);
      } else {
        setPostOdometerLoading(false);
      }
    }
  };

  const handleOpenModalImageGallery = (index: number) => {
    if (bookingDetails?.fleet?.fleet_photos?.length === 0) {
      toast("No Images available for this fleet", {
        style: {
          background: "#fff3cd",
          color: "#856404",
        },
      });
    } else {
      setCurrentIndex(index);
      setIsOpenModal(true);
    }
  };

  const closeModal = () => {
    setIsOpenModal(false);
  };

  const closeGridModal = () => setIsGridOpen(false);

  const modifyBooking = async (bookingId: string) => {
    navigate(`/modify-booking/${bookingId}`);
    return;
  };

  const handleCancelBooking = async () => {
    if (!bookingId) return;
    if (cancellationPolicyChecked !== true) {
      toast.error("Please accept our Cancellation Policy");
      return;
    }
    setIsCancelling(true);
    try {
      const result = await cancelBooking(bookingId);

      if (result.error) {
        toast.error(result.error);
      } else {
        // 1NOW-1171: explicit success confirmation; the UI refreshes via
        // fetchBookingDetail() below (no manual reload). A pending request
        // (approval required) comes back as a {detail} message; a direct
        // cancel returns the cancelled booking.
        toast.success(
          result?.data?.detail
            ? "Cancellation request submitted for approval."
            : "Booking cancelled.",
        );
        setIsCancelModalOpen(false);
        setIsEdit(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      toast.error("Failed to cancel booking");
    } finally {
      setIsCancelling(false);
      fetchBookingDetail();
    }
  };

  const handleOpenTotalBillModal = () => {
    setIsTotalBillModalOpen(true);
  };

  const handleCloseTotalBillModal = () => {
    setIsTotalBillModalOpen(false);
  };

  function close() {
    setIsOpen(false);
  }

  /* The agreement key is the booking's uuid. It used to be
     `{pk}b6cr4{companyId}b6cr4{base64(email)}`, but the backend only ever read
     the first segment — the company id was logged and never checked, and the
     email was ignored outright — so a guessed integer opened any operator's
     agreement (1NOW-10707). Both dead segments are gone with it. */
  const openRentalAgreement = async () => {
    if (!bookingUid) return;
    const url = getAgreementPdfBase();
    window.open(`${url}${encodeURIComponent(bookingUid)}`, "_blank");
  };

  function extractSubstringInBrackets(input: string) {
    if (typeof input !== "string") {
      throw new Error("Input must be a string.");
    }
    const match = input.match(/\(([^)]+)\)/);
    return match ? match[1] : null;
  }

  const sendAgreementEmail = async (type: string) => {
    if (!bookingUid) return;
    try {
      const data: any = await sendRentalAgreement(bookingUid, type);
      toast.success("Email successfully sent!");
    } catch (error: any) {
      toast.error(error.toString());
    }
  };

  const fetchBookingDetail = async () => {
    setBookingLoading(true);
    if (!bookingId) {
      toast.error("Booking ID is missing");
      setBookingLoading(false);
      return;
    }
    try {
      const data = await retriveBooking(bookingId);
      if (data.error) {
        throw new Error(data.error);
      }
      setBookingDetail(data);
      setBookingLoading(false);
    } catch (error) {
      toast.error("Booking Not Found");
      setBookingDetail(null);
      setBookingLoading(false);
    }
  };
  useEffect(() => {
    fetchBookingDetail();
  }, [bookingId]);

  // Fetch cancellation policy content (env-switched company id, like other AMK callers).
  useEffect(() => {
    const getContent = async () => {
      try {
        const companyId =
          process.env.REACT_APP_ENV !== "prod"
            ? globalJson.dev_company_id
            : globalJson.prod_company_id;
        const response = await getPrivacyPolicyContent(companyId);
        if (response?.data?.cancellation_policy) {
          setCancellationPolicy(response.data.cancellation_policy);
        }
      } catch (error) {
        console.error("Error fetching cancellation policy", error);
      }
    };

    getContent();
  }, []);

  // Fetch customer documents once the customer is known.
  useEffect(() => {
    if (customerUid) {
      fetchIdDocuments(customerUid);
      fetchInsuranceDocuments(customerUid);
    }
  }, [customerUid]);

  // Auto-open the cancellation policy once per booking (when verification is
  // still outstanding), tracked via a per-booking localStorage flag.
  useEffect(() => {
    if (bookingDetails && bookingDetails.trip_status !== "cancelled") {
      const modalShownKey = `cancellationPolicyShown_${bookingDetails.id}`;
      const hasBeenShown = localStorage.getItem(modalShownKey);

      if (
        !hasBeenShown &&
        (!bookingDetails.customer?.identity_verified ||
          !bookingDetails.customer?.insurance_verified)
      ) {
        setIsCancellationPolicyModalOpen(true);
        localStorage.setItem(modalShownKey, "true");
      }
    }
  }, [bookingDetails]);

  // Fetch trip photos to populate the pre/post inspection card summaries
  // and the latest odometer captures.
  useEffect(() => {
    if (!bookingUid) return;
    let cancelled = false;
    (async () => {
      const result = await getTripPhotos(bookingUid);
      if (cancelled) return;
      const photos: any[] = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
        ? result.data
        : [];
      const latest = (type: string) =>
        photos
          .filter((p) => p.image_type === type)
          .sort(
            (a, b) =>
              new Date(b.created_at || 0).getTime() -
              new Date(a.created_at || 0).getTime()
          )[0];
      const preImg = latest("pre_odometer");
      const postImg = latest("post_odometer");
      if (preImg?.image) setPreOdometerImage(preImg.image);
      if (postImg?.image) setPostOdometerImage(postImg.image);
      setPreTripBreakdown(classifyTripPhotos(photos, "pre_trip"));
      setPostTripBreakdown(classifyTripPhotos(photos, "post_trip"));
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingUid]);

  useEffect(() => {
    if (bookingDetails) {
      setDropoffLocationType(bookingDetails?.same_drop_off);
      setPickupLocation(bookingDetails?.pick_up_location);
      setSearchedPickupLocation(bookingDetails?.custom_pick_up_address);
      setSearchedDropoffLocation(bookingDetails?.custom_drop_off_address);
      setDropoffLocation(bookingDetails?.drop_off_location);
      setStartTime(
        formatTimeToHumanFirendly24Hour(bookingDetails?.pick_up_time)
      );
      setEndTime(
        formatTimeToHumanFirendly24Hour(bookingDetails?.drop_off_time)
      );
      setStartDate(
        new Date(formatDateToHumanFirendly(bookingDetails?.pick_up_time))
      );
      setEndDate(
        new Date(formatDateToHumanFirendly(bookingDetails?.drop_off_time))
      );
      setSelectedCar(bookingDetails?.fleet);
      if (bookingDetails?.extras?.length > 0) {
        const extraIds = new Set<number>(
          bookingDetails.extras.map((extra: any) => extra.id)
        );
        setSelectedExtras(extraIds);
      }

      if (bookingDetails?.bonzah_insurance_options?.length > 0) {
        const insuranceIds = bookingDetails?.bonzah_insurance_options?.map(
          (insurance: any) => insurance
        );
        setSelectedBonzahProducts(insuranceIds);
      }
    }
  }, [isEdit]);

  const handleCanopyVerificationButtonClick = async () => {
    // Drop the click if a previous request hasn't resolved — otherwise
    // rapid clicks fire N requests and stack N duplicate error toasts
    // (ONENOW-466). Passing a stable `id` to `toast.error` also collapses
    // any duplicates that do slip through into a single toast.
    if (canopyLoading) return;
    setCanopyLoading(true);
    const payload: CanopyPayload = {
      user_id: customerUid ?? "",
    };
    const IgnitionResponse = (await CreateCanopyVerification(payload)) as {
      data?: { insurance_verification_link?: string };
      error?: string;
    };
    if (
      IgnitionResponse.data &&
      IgnitionResponse.data.insurance_verification_link
    ) {
      const ignitionUrl = IgnitionResponse.data.insurance_verification_link;
      setCanopyLink(ignitionUrl);
      setCanopyLoading(false);
      const newTab = window.open(ignitionUrl, "_blank");
      if (!newTab) {
        setShowCopyCanopy(true);
        toast("Popup blocked. Please copy the link manually.", {
          style: { background: "#fff3cd", color: "#856404" },
          id: "canopy-popup-blocked",
        });
      }
      return;
    } else {
      console.error(
        "Failed to retrieve insurance verification link:",
        IgnitionResponse.error
      );
      toast.error("Verification failed. Please try again later.", {
        id: "canopy-verification-error",
      });
      setCanopyLoading(false);
    }
  };

  const handlePrimaryDriverVerificationButtonClick = async () => {
    if (identityLoading) return; // ONENOW-466 — drop rapid clicks
    setIdentityLoading(true);
    const payload = { customer_id: customerUid ?? "" };
    if (ourCompany?.default_identity === "vouched") {
      const vouchSessionResponse = (await CreateVouchVerification(payload)) as {
        data?: { session_url?: string };
        error?: string;
      };
      if (vouchSessionResponse.data && vouchSessionResponse.data.session_url) {
        const ignitionUrl = vouchSessionResponse.data.session_url;
        setIdentityLink(ignitionUrl);
        setIdentityLoading(false);
        const newTab = window.open(ignitionUrl, "_blank");
        if (!newTab) {
          setShowCopyIdentity(true);
          toast("Popup blocked. Please copy the link manually.", {
            style: { background: "#fff3cd", color: "#856404" },
            id: "identity-popup-blocked",
          });
        }
        return;
      } else {
        console.error(
          "Failed to retrieve session URL:",
          vouchSessionResponse.error
        );
        toast.error(
          vouchSessionResponse?.error ??
            "Verification failed. Please try again later.",
          { id: "identity-verification-error" }
        );
        setIdentityLoading(false);
      }
    } else {
      const stripeSessionResponse = (await CreateStripeVerification(
        payload
      )) as {
        data?: { session_url?: string };
        error?: string;
      };
      if (
        stripeSessionResponse.data &&
        stripeSessionResponse.data.session_url
      ) {
        const ignitionUrl = stripeSessionResponse.data.session_url;
        setIdentityLink(ignitionUrl);
        setIdentityLoading(false);
        const newTab = window.open(ignitionUrl, "_blank");
        if (!newTab) {
          setShowCopyIdentity(true);
          toast("Popup blocked. Please copy the link manually.", {
            style: { background: "#fff3cd", color: "#856404" },
            id: "identity-popup-blocked",
          });
        }
        return;
      } else {
        console.error(
          "Failed to retrieve session URL:",
          stripeSessionResponse.error
        );
        toast.error(
          stripeSessionResponse?.error ??
            "Verification failed. Please try again later.",
          { id: "identity-verification-error" }
        );
        setIdentityLoading(false);
      }
    }
  };
  const handleDriverCanopyVerificationButtonClick = async () => {
    if (drivercanopyLoading) return; // ONENOW-466 — drop rapid clicks
    setDriverCanopyLoading(true);
    const payload: CanopyPayload = {
      user_id: secondaryDriverUid ?? "",
    };
    const IgnitionResponse = (await CreateCanopyVerification(payload)) as {
      data?: { insurance_verification_link?: string };
      error?: string;
    };
    if (
      IgnitionResponse.data &&
      IgnitionResponse.data.insurance_verification_link
    ) {
      const ignitionUrl = IgnitionResponse.data.insurance_verification_link;
      setDriverCanopyLink(ignitionUrl);
      setDriverCanopyLoading(false);
      const newTab = window.open(ignitionUrl, "_blank");
      if (!newTab) {
        setShowCopyDriverCanopy(true);
        toast("Popup blocked. Please copy the link manually.", {
          style: { background: "#fff3cd", color: "#856404" },
          id: "driver-canopy-popup-blocked",
        });
      }
      return;
    } else {
      console.error(
        "Failed to retrieve insurance verification link:",
        IgnitionResponse.error
      );
      toast.error("Verification failed. Please try again later.", {
        id: "driver-canopy-verification-error",
      });
      setDriverCanopyLoading(false);
    }
  };

  const handleDriverPrimaryDriverVerificationButtonClick = async () => {
    if (driveridentityLoading) return; // ONENOW-466 — drop rapid clicks
    setDriverIdentityLoading(true);
    const payload = {
      customer_id: secondaryDriverUid ?? "",
    };
    if (ourCompany?.default_identity === "vouched") {
      const vouchSessionResponse = (await CreateVouchVerification(payload)) as {
        data?: { session_url?: string };
        error?: string;
      };
      if (vouchSessionResponse.data && vouchSessionResponse.data.session_url) {
        const ignitionUrl = vouchSessionResponse.data.session_url;
        setDriverIdentityLink(ignitionUrl);
        setDriverIdentityLoading(false);
        const newTab = window.open(ignitionUrl, "_blank");
        if (!newTab) {
          setShowCopyDriverIdentity(true);
          toast("Popup blocked. Please copy the link manually.", {
            style: { background: "#fff3cd", color: "#856404" },
            id: "driver-identity-popup-blocked",
          });
        }
        return;
      } else {
        console.error(
          "Failed to retrieve session URL:",
          vouchSessionResponse.error
        );
        toast.error("Verification failed. Please try again later.", {
          id: "driver-identity-verification-error",
        });
        setDriverIdentityLoading(false);
      }
    } else {
      const stripeSessionResponse = (await CreateStripeVerification(
        payload
      )) as {
        data?: { session_url?: string };
        error?: string;
      };
      if (
        stripeSessionResponse.data &&
        stripeSessionResponse.data.session_url
      ) {
        const ignitionUrl = stripeSessionResponse.data.session_url;
        setDriverIdentityLink(ignitionUrl);
        setDriverIdentityLoading(false);
        const newTab = window.open(ignitionUrl, "_blank");
        if (!newTab) {
          setShowCopyDriverIdentity(true);
          toast("Popup blocked. Please copy the link manually.", {
            style: { background: "#fff3cd", color: "#856404" },
            id: "driver-identity-popup-blocked",
          });
        }
        return;
      } else {
        console.error(
          "Failed to retrieve session URL:",
          stripeSessionResponse.error
        );
        toast.error(
          stripeSessionResponse.error ??
            "Verification failed. Please try again later.",
          { id: "driver-identity-verification-error" }
        );
        setDriverIdentityLoading(false);
      }
    }
  };

  const renderImgSection = () => {
    return (
      <div>
        <header className="rounded-md sm:rounded-xl">
          <div className="relative grid grid-cols-1 md:grid-cols-4  gap-1 sm:gap-2 min-h-96 ">
            <div
              className="col-span-2 row-span-2 relative rounded-md sm:rounded-xl overflow-hidden cursor-pointer"
              onClick={() => handleOpenModalImageGallery(0)}
            >
              <img
                src={fleetPhotoSrc(
                  bookingDetails?.fleet?.fleet_photos?.[0]?.photo_url
                )}
                alt="0"
                className="absolute inset-0 object-cover rounded-md sm:rounded-xl w-full h-full"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 50vw"
              />
              <div className="absolute inset-0 bg-neutral-900 bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity"></div>
            </div>

            {/*  */}
            <div
              className="col-span-1 row-span-2 relative rounded-md sm:rounded-xl overflow-hidden cursor-pointer"
              onClick={() => handleOpenModalImageGallery(0)}
            >
              <img
                className="absolute inset-0 object-cover rounded-md sm:rounded-xl w-full h-full"
                src={fleetPhotoSrc(
                  bookingDetails?.fleet?.fleet_photos?.[1]?.photo_url
                )}
                alt="1"
                sizes="400px"
              />
              <div className="absolute inset-0 bg-neutral-900 bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity"></div>
            </div>

            {/*  */}
            {bookingDetails?.fleet?.fleet_photos
              .filter((_: any, i: any) => i >= 2 && i < 4)
              .map((item: any, index: any) => (
                <div
                  key={index}
                  className={`relative rounded-md sm:rounded-xl overflow-hidden ${
                    index >= 2 ? "block" : ""
                  }`}
                >
                  <div className="aspect-w-4 aspect-h-3">
                    <img
                      className="absolute inset-0 object-cover w-full h-full rounded-md sm:rounded-xl "
                      src={fleetPhotoSrc(item.photo_url)}
                      alt="photos"
                      sizes="400px"
                    />
                  </div>
                  <div
                    className="absolute inset-0 bg-neutral-900 bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                    onClick={() => handleOpenModalImageGallery(0)}
                  />
                </div>
              ))}

            {(bookingDetails?.fleet?.fleet_photos?.length ?? 0) > 1 && (
              <div
                className="absolute flex md:items-center md:justify-center left-3 bottom-3 px-4 py-2 rounded-xl bg-neutral-100 text-neutral-500 cursor-pointer hover:bg-neutral-200 z-10"
                onClick={() => setIsGridOpen(true)}
              >
                <Squares2X2Icon className="h-5 w-5" />
                <span className="ml-2 text-neutral-800 text-sm font-medium">
                  Show all photos
                </span>
              </div>
            )}
          </div>
        </header>

        {isGridOpen && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4">
            <div className="relative w-full max-w-5xl bg-white rounded-lg overflow-auto p-4">
              <button
                className="absolute top-1 right-3 text-lg text-text-color bg-white rounded-full w-10 h-10 flex items-center justify-center z-10"
                onClick={closeGridModal}
                aria-label="Close gallery"
                type="button"
              >
                ✖
              </button>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 pt-8">
                {(bookingDetails?.fleet?.fleet_photos ?? []).map(
                  (item: any, idx: number) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setCurrentIndex(idx);
                        setIsOpenModal(true);
                        setIsGridOpen(false);
                      }}
                      className="w-full h-32 sm:h-40 md:h-48 overflow-hidden rounded-md focus:outline-none focus:ring-2 focus:ring-primary-100 transition-transform hover:scale-105"
                      type="button"
                    >
                      <img
                        src={fleetPhotoSrc(item.photo_url)}
                        alt={`photo-${idx}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ),
                )}
                {(bookingDetails?.fleet?.fleet_photos?.length ?? 0) === 0 && (
                  <div className="col-span-full text-center p-8 text-text-color">
                    No images available
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {isOpenModal && (
          <div className="fixed h-full top-0 left-0 w-full  bg-black bg-opacity-90 flex items-center justify-center z-50">
            <button
              className="absolute top-5 right-5 text-white text-xl"
              onClick={closeModal}
            >
              ✖
            </button>

            <div className="w-4/5 ">
              <Slide
                defaultIndex={currentIndex}
                indicators={bookingDetails?.fleet?.fleet_photos?.length > 1}
                arrows={bookingDetails?.fleet?.fleet_photos?.length > 1}
                autoplay={false}
                duration={2000}
                transitionDuration={300}
                onChange={(current) => setCurrentIndex(current)}
                prevArrow={
                  <div
                    className={
                      bookingDetails?.fleet?.fleet_photos?.length <= 1 ||
                      currentIndex === 0
                        ? "invisible"
                        : ""
                    }
                  >
                    <span className="default-nav">❮</span>
                  </div>
                }
                nextArrow={
                  <div
                    className={
                      bookingDetails?.fleet?.fleet_photos?.length <= 1 ||
                      currentIndex ===
                        bookingDetails?.fleet?.fleet_photos?.length - 1
                        ? "invisible"
                        : ""
                    }
                  >
                    <span className="default-nav">❯</span>
                  </div>
                }
              >
                {(bookingDetails?.fleet?.fleet_photos?.length || 0) > 0 ? (
                  bookingDetails?.fleet?.fleet_photos?.map(
                    (item: any, index: number) => (
                      <div
                        className="flex justify-center items-center h-full"
                        key={index}
                      >
                        <img
                          className="w-full max-h-full object-contain rounded-lg"
                          src={fleetPhotoSrc(item.photo_url)}
                          alt={`Slide ${index}`}
                        />
                      </div>
                    )
                  )
                ) : (
                  <div className="flex justify-center items-center h-full">
                    <img
                      className="w-full max-h-full object-contain rounded-lg"
                      src="https://tenant.fleetwire.io/images/vehicle-placeholder.jpg"
                      alt="No images available"
                    />
                  </div>
                )}
              </Slide>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBookingDetailsSection = () => {
    return (
      <div className="mt-8">
        <div className="grid grid-cols-2 ">
          <div className="col-span-1">
            <span className=" text-lg text-[#777777]">Pick Up</span>
            <br />
            <span className="mt-1.5 text-lg font-bold ">
              <span>
                {formatDateToHumanFirendly(bookingDetails?.pick_up_time)}
              </span>
              <br />
              <span className="font-normal">
                {ourCompany?.default_time_format === "24_hour"
                  ? formatTimeToHumanFirendly24Hour(
                      bookingDetails?.pick_up_time
                    )
                  : formatTimeToHumanFirendly12Hour(
                      bookingDetails?.pick_up_time
                    )}
              </span>
            </span>
            <br />
            <span className="mt-1.5 text-lg  ">
              {bookingDetails?.custom_pick_up_address === ""
                ? bookingDetails?.pick_up_location?.name
                : bookingDetails?.custom_pick_up_address}
            </span>
          </div>
          <div className="col-span-1">
            <span className=" text-lg text-[#777777]">Drop Off</span>
            <br />
            <span className="mt-1.5 text-lg font-bold ">
              <span>
                {formatDateToHumanFirendly(bookingDetails?.drop_off_time)}
              </span>
              <br />
              <span className="font-normal">
                {ourCompany?.default_time_format === "24_hour"
                  ? formatTimeToHumanFirendly24Hour(
                      bookingDetails?.drop_off_time
                    )
                  : formatTimeToHumanFirendly12Hour(
                      bookingDetails?.drop_off_time
                    )}
              </span>
            </span>
            <br />
            <span className="mt-1.5 text-lg ">
              {bookingDetails?.custom_drop_off_address === ""
                ? bookingDetails?.drop_off_location?.name
                : bookingDetails?.custom_drop_off_address
                ? bookingDetails?.custom_drop_off_address
                : bookingDetails?.custom_pick_up_address}
            </span>
          </div>
        </div>
        {bookingDetails?.trip_status !== "cancelled" && (
          <div className="mt-5 flex w-full flex-col gap-4 rounded-lg border border-neutral-200 bg-bg-color p-5">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-lg font-bold text-text-color">
                  Upload Trip Photos
                </div>
                <div className="text-sm text-text-color/70">
                  Pre-trip and post-trip vehicle photos with odometer captures.
                </div>
              </div>
              <button
                onClick={() => {
                  setInspectionPhase("pre");
                  setIsInspectionOpen(true);
                }}
                className="inline-flex items-center gap-2 rounded-lg bg-primary-200 px-4 py-2.5 text-sm font-semibold text-neutral-50 transition-opacity hover:opacity-90"
              >
                <CameraIcon className="h-4 w-4" />
                Upload Trip Photos
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <button
                type="button"
                onClick={() => {
                  setInspectionPhase("pre");
                  setIsInspectionOpen(true);
                }}
                className="flex items-center justify-between rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-left transition hover:border-primary-100 hover:bg-primary-50"
              >
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-color/60">
                    Pre-trip
                  </div>
                  <div className="mt-1 text-sm font-semibold text-text-color">
                    {summarizeFileMix(preTripBreakdown)}
                  </div>
                  <div className="text-xs text-text-color/70">
                    {!preOdometerImage
                      ? "0 mi"
                      : bookingDetails?.miles_before != null
                      ? `${Number(
                          bookingDetails.miles_before
                        ).toLocaleString()} mi`
                      : "Mileage not set"}
                  </div>
                </div>
                <svg
                  className="h-4 w-4 -rotate-45 text-text-color/60"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>

              <button
                type="button"
                onClick={() => {
                  setInspectionPhase("post");
                  setIsInspectionOpen(true);
                }}
                className="flex items-center justify-between rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-left transition hover:border-primary-100 hover:bg-primary-50"
              >
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-color/60">
                    Post-trip
                  </div>
                  <div className="mt-1 text-sm font-semibold text-text-color">
                    {summarizeFileMix(postTripBreakdown)}
                  </div>
                  <div className="text-xs text-text-color/70">
                    {!postOdometerImage
                      ? "0 mi"
                      : bookingDetails?.miles_after != null
                      ? `${Number(
                          bookingDetails.miles_after
                        ).toLocaleString()} mi`
                      : "Mileage not set"}
                  </div>
                </div>
                <svg
                  className="h-4 w-4 -rotate-45 text-text-color/60"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
            </div>
          </div>
        )}
        <div className="flex flex-col lg:flex-row lg:justify-between gap-0 md:gap-12">
          <div className="container mx-auto px-4 py-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className=" mb-6 md:mb-0">
                <h2 className="text-lg font-bold mb-8">Trip Info</h2>
                <div className="mb-4 flex justify-between items-center">
                  <div>
                    <p className="text-gray-800 dark:text-white">Total Bill</p>
                    <p className="text-gray-800 dark:text-white">
                      {bookingDetails?.invoice
                        ?.total_price_after_coupon_without_security !== null
                        ? bookingDetails?.invoice
                            ?.total_price_after_coupon_without_security
                        : "$" +
                          bookingDetails?.invoice?.total_price_without_security}
                    </p>
                  </div>
                  <div
                    onClick={handleOpenTotalBillModal}
                    className="text-gray-800 dark:text-white cursor-pointer mx-2"
                  >
                    <div className="flex text-primary-200 font-semibold">
                      View Invoice
                      <svg
                        width="23"
                        height="26"
                        viewBox="0 0 23 26"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                          fill="#90A3BF"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="mb-4 flex justify-between items-center">
                  <div>
                    <p className="text-gray-800 dark:text-white">Agreement</p>
                  </div>
                  <div
                    onClick={openRentalAgreement}
                    className="text-gray-800 dark:text-white cursor-pointer mx-2"
                  >
                    <div className="flex text-primary-200 font-semibold">
                      View Agreement
                      <svg
                        width="23"
                        height="26"
                        viewBox="0 0 23 26"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                          fill="#90A3BF"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="mb-4 flex justify-between items-center">
                  <div>
                    <p className="text-gray-800 dark:text-white">
                      Get Booking Email
                    </p>
                  </div>
                  <span
                    className="text-primary-200 mx-2 cursor-pointer font-semibold"
                    onClick={() => sendAgreementEmail("booking_confirmation")}
                  >
                    Resend email
                  </span>
                </div>
                <div className="mb-4 flex justify-between items-center">
                  <div>
                    <p className="text-gray-800 dark:text-white">
                      Get Agreement
                    </p>
                  </div>
                  <span
                    className="text-primary-200 mx-2 cursor-pointer font-semibold"
                    onClick={() => sendAgreementEmail("rental_agreement")}
                  >
                    Resend email
                  </span>
                </div>
                {bookingDetails?.bonzah_purchase_response_data?.data?.coverage_information
                  ?.filter((insurance: any) => insurance?.added === true)
                  ?.map((insurance: any, index: any) => (
                    <div
                      key={index}
                      onClick={async () => {
                        try {
                          const { data, error }: any = await getBonzahPDF(
                            bookingUid,
                            extractSubstringInBrackets(
                              insurance?.optional_addon_cover_name?.toLowerCase() ??
                                ""
                            ) ?? ""
                          );

                          if (error) {
                            throw new Error(error);
                          }
                          if (data instanceof ArrayBuffer) {
                            console.log(
                              "ArrayBuffer detected, converting to Blob..."
                            );
                            const pdfBlob = new Blob([data], {
                              type: "application/pdf",
                            });

                            const pdfUrl = URL.createObjectURL(pdfBlob);
                            const newTab = window.open(pdfUrl, "_blank");

                            if (newTab) {
                              newTab.focus();
                            } else {
                              const pdfUrl = URL.createObjectURL(pdfBlob);
                              const link = document.createElement("a");
                              link.href = pdfUrl;
                              link.target = "_blank";
                              link.download = "insurance_agreement.pdf";
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }
                          } else {
                            throw new Error("Invalid PDF data received.");
                          }
                        } catch (error: any) {
                          console.error("Error fetching PDF:", error);
                          toast.error(
                            error.message ||
                              "An error occurred while fetching the PDF."
                          );
                        }
                      }}
                      className="w-full cursor-pointer justify-between overflow-clip rounded-lg"
                    >
                      <div className="mt-3 flex items-center justify-between">
                        <div>
                          <div className="text-gray-800 dark:text-white">
                            Insurance Agreement:{" "}
                            {insurance?.optional_addon_cover_name}
                          </div>
                          <div className="text-xs font-medium text-gray-400">
                            {insurance?.policy_created_date}
                          </div>
                        </div>
                        <div className="min-h-5 min-w-5 font-bold">
                          <svg
                            width="23"
                            height="26"
                            viewBox="0 0 23 26"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                              fill="#90A3BF"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              <div className=" mb-6 md:mb-0">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-lg font-bold ">Renter Info</h2>
                  <p
                    className="text-lg text-green-600 cursor-pointer"
                    onClick={() => setIsRenterInfoEdit(!isRenterInfoEdit)}
                  >
                    {isRenterInfoEdit ? "Cancel" : "Edit"}
                  </p>
                </div>
                {!isRenterInfoEdit ? (
                  <>
                    <div className="mb-4 flex ">
                      <div>
                        <p className="text-gray-800 dark:text-white">
                          {bookingDetails?.customer?.first_name}{" "}
                          {bookingDetails?.customer?.last_name}
                        </p>
                      </div>
                    </div>
                    <div className="mb-4 flex">
                      <div>
                        <p className="text-gray-800 dark:text-white">
                          {bookingDetails?.customer?.email}
                        </p>
                      </div>
                    </div>
                    <div className="mb-4 flex ">
                      <div>
                        <p className="text-gray-800 dark:text-white">
                          <PhoneNumberDisplay
                            phone={bookingDetails?.customer?.phone_no}
                          />
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <RenterInfo
                    bookingDetails={bookingDetails}
                    setIsRenterInfoEdit={setIsRenterInfoEdit}
                    onSuccessfulUpdate={fetchBookingDetail}
                  />
                )}
                {identityLink ? (
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <p className="text-gray-800 dark:text-white truncate max-w-48">
                        {identityLink}
                      </p>
                    </div>
                    <span
                      className={`mx-2 cursor-pointer font-semibold text-red-600`}
                      onClick={() => {
                        navigator.clipboard.writeText(identityLink);
                        toast.success("Link Copied");
                      }}
                    >
                      Copy
                    </span>
                  </div>
                ) : (
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <p className="text-gray-800 dark:text-white">
                        ID Verification
                      </p>
                    </div>
                    <span
                      className={`mx-2 cursor-pointer font-semibold ${
                        bookingDetails?.customer?.identity_verified
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                      {...(!bookingDetails?.customer?.identity_verified && {
                        onClick: handlePrimaryDriverVerificationButtonClick,
                      })}
                    >
                      <div className="flex">
                        {bookingDetails &&
                        bookingDetails?.customer?.identity_verified !== true
                          ? "Create Verification Link"
                          : "Verified"}
                        {bookingDetails?.customer?.identity_verified !==
                          true && (
                          <svg
                            width="23"
                            height="26"
                            viewBox="0 0 23 26"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                              fill="#90A3BF"
                            />
                          </svg>
                        )}
                      </div>
                    </span>
                  </div>
                )}

                {canopyLink ? (
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <p className="text-gray-800 dark:text-white truncate max-w-48">
                        {canopyLink}
                      </p>
                    </div>
                    <span
                      className={`mx-2 cursor-pointer font-semibold text-red-600`}
                      onClick={() => {
                        navigator.clipboard.writeText(canopyLink);
                        toast.success("Link Copied");
                      }}
                    >
                      Copy
                    </span>
                  </div>
                ) : (
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <p className="text-gray-800 dark:text-white">
                        Insurance Verification
                      </p>
                    </div>
                    <span
                      className={`mx-2 cursor-pointer font-semibold ${
                        bookingDetails?.customer?.insurance_verified
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                      {...(!bookingDetails?.customer?.insurance_verified && {
                        onClick: handleCanopyVerificationButtonClick,
                      })}
                    >
                      <div className="flex">
                        {bookingDetails &&
                        bookingDetails?.customer?.insurance_verified !== true
                          ? "Create Verification Link"
                          : "Verified"}
                        {bookingDetails?.customer?.insurance_verified !==
                          true && (
                          <svg
                            width="23"
                            height="26"
                            viewBox="0 0 23 26"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                              fill="#90A3BF"
                            />
                          </svg>
                        )}
                      </div>
                    </span>
                  </div>
                )}

                {bookingDetails?.secondary_driver_obj && (
                  <>
                    {driveridentityLink ? (
                      <div className="mb-4 flex justify-between items-center">
                        <div>
                          <p className="text-gray-800 dark:text-white truncate max-w-48">
                            {driveridentityLink}
                          </p>
                        </div>
                        <span
                          className={`mx-2 cursor-pointer font-semibold text-red-600`}
                          onClick={() => {
                            navigator.clipboard.writeText(driveridentityLink);
                            toast.success("Link Copied");
                          }}
                        >
                          Copy
                        </span>
                      </div>
                    ) : (
                      <div className="mb-4 flex justify-between items-center">
                        <div>
                          <p className="text-gray-800 dark:text-white">
                            Secondary Driver ID Verification
                          </p>
                        </div>
                        <span
                          className={`mx-2 cursor-pointer font-semibold ${
                            bookingDetails.secondary_driver_obj
                              .identity_verified
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                          {...(!bookingDetails.secondary_driver_obj
                            .identity_verified && {
                            onClick:
                              handleDriverPrimaryDriverVerificationButtonClick,
                          })}
                        >
                          <div className="flex">
                            {bookingDetails.secondary_driver_obj
                              .identity_verified
                              ? "Verified"
                              : "Create Verification Link"}
                            {!bookingDetails.secondary_driver_obj
                              .identity_verified && (
                              <svg
                                width="23"
                                height="26"
                                viewBox="0 0 23 26"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                                  fill="#90A3BF"
                                />
                              </svg>
                            )}
                          </div>
                        </span>
                      </div>
                    )}

                    {drivercanopyLink ? (
                      <div className="mb-4 flex justify-between items-center">
                        <div>
                          <p className="text-gray-800 dark:text-white truncate max-w-48">
                            {drivercanopyLink}
                          </p>
                        </div>
                        <span
                          className={`mx-2 cursor-pointer font-semibold text-red-600`}
                          onClick={() => {
                            navigator.clipboard.writeText(drivercanopyLink);
                            toast.success("Link Copied");
                          }}
                        >
                          Copy
                        </span>
                      </div>
                    ) : (
                      <div className="mb-4 flex justify-between items-center">
                        <div>
                          <p className="text-gray-800 dark:text-white">
                            Secondary Driver Insurance Verification
                          </p>
                        </div>
                        <span
                          className={`mx-2 cursor-pointer font-semibold ${
                            bookingDetails.secondary_driver_obj
                              .insurance_verified
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                          {...(!bookingDetails.secondary_driver_obj
                            .insurance_verified && {
                            onClick: handleDriverCanopyVerificationButtonClick,
                          })}
                        >
                          <div className="flex">
                            {bookingDetails.secondary_driver_obj
                              .insurance_verified
                              ? "Verified"
                              : "Create Verification Link"}
                            {!bookingDetails.secondary_driver_obj
                              .insurance_verified && (
                              <svg
                                width="23"
                                height="26"
                                viewBox="0 0 23 26"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                                  fill="#90A3BF"
                                />
                              </svg>
                            )}
                          </div>
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="container mx-auto px-4 py-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className=" w-full mt-4">
                  <h2 className="text-lg font-bold my-4">About The Car</h2>
                  <div className="mb-4">
                    <p className="text-gray-800 truncate dark:text-white">
                      {bookingDetails?.fleet?.name}
                    </p>
                    <a href="#" className="text-gray-800"></a>
                  </div>
                  <div className="mb-4 flex justify-between">
                    <p className="text-gray-800 dark:text-white">
                      License plate number
                    </p>
                    <span className="font-bold">
                      {bookingDetails?.fleet?.plate_number}
                    </span>
                  </div>
                  <div className="mb-4 flex justify-between">
                    <p className="text-gray-800 dark:text-white">Car color</p>
                    <span className="font-bold ">
                      {bookingDetails?.fleet?.color}
                    </span>
                  </div>
                  <div className="mb-4 flex justify-between">
                    <p className="text-gray-800 dark:text-white">Build</p>
                    <span className="font-bold truncate max-w-[180px] block">
                      {bookingDetails?.fleet?.make}
                    </span>
                  </div>
                </div>
              </div>
              {bookingDetails?.note === null || bookingDetails?.note === "" ? (
                ""
              ) : (
                <div className="w-full">
                  <h2 className="text-xl font-bold mb-8">Note</h2>
                  <span className="text-gray-800">{bookingDetails?.note}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        {bookingDetails?.customer && (
          <>
            {(insuranceDocuments.length > 0 ||
              bookingDetails?.custom_insurance_options?.[0]
                ?.upload_after_payment === true) && (
            <div className="mt-8 border border-neutral-200 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-text-color mb-5">
                Upload Proof Of Insurance
              </h2>

              {bookingDetails?.custom_insurance_options?.[0]
                ?.upload_after_payment === true && (
              <div
                {...getInsuranceRootProps()}
                className={`flex flex-col items-center justify-center w-full border-2 border-dashed rounded-xl py-8 px-4 cursor-pointer transition-colors mb-4 ${
                  isInsuranceDragActive
                    ? "border-primary-200 bg-blue-50"
                    : "border-neutral-200 hover:border-primary-200"
                }`}
              >
                <input {...getInsuranceInputProps()} />
                <div className="w-12 h-12 rounded-full border-2 border-neutral-300 flex items-center justify-center mb-2">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
                      stroke="#9AA3AD"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <polyline
                      points="17 8 12 3 7 8"
                      stroke="#9AA3AD"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <line
                      x1="12"
                      y1="3"
                      x2="12"
                      y2="15"
                      stroke="#9AA3AD"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span className="text-sm font-medium text-text-color">
                  {isInsuranceDragActive
                    ? "Drop files here…"
                    : "Drop or select file"}
                </span>
                <span className="text-xs text-neutral-400 mt-1">
                  Supported: PDF. Max: 10MB
                </span>
              </div>
              )}

              {pendingInsuranceFiles.map((file, i) => (
                <div
                  key={`pending-insurance-${i}`}
                  className="flex items-center gap-3 border border-neutral-200 rounded-lg px-4 py-3 mb-2"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-red-50 rounded-md flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
                        fill="#e53e3e"
                        opacity="0.2"
                      />
                      <path
                        d="M14 2v6h6M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
                        stroke="#e53e3e"
                        strokeWidth="1.5"
                        fill="none"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-color truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-neutral-400 mt-0.5">
                      {(file.size / (1024 * 1024)).toFixed(1)}MB · Ready to
                      upload
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setPendingInsuranceFiles((prev) =>
                        prev.filter((_, idx) => idx !== i)
                      )
                    }
                    className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-neutral-100 hover:bg-red-50 text-neutral-400 hover:text-red-500 text-xs transition-colors"
                  >
                    &#x2715;
                  </button>
                </div>
              ))}

              {insuranceDocuments.map((doc: any) => (
                <div
                  key={doc.id}
                  className="flex items-center gap-3 border border-neutral-200 rounded-lg px-4 py-3 mb-2"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-red-50 rounded-md flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
                        fill="#e53e3e"
                        opacity="0.2"
                      />
                      <path
                        d="M14 2v6h6M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
                        stroke="#e53e3e"
                        strokeWidth="1.5"
                        fill="none"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-color truncate">
                      {doc.name ?? doc.file_name ?? "Insurance Document"}
                    </p>
                    <p className="text-xs text-neutral-400 mt-0.5">
                      Uploaded Successfully
                    </p>
                  </div>
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-200 flex items-center justify-center">
                    <CheckIcon className="w-3.5 h-3.5 text-white" />
                  </div>
                </div>
              ))}

              {pendingInsuranceFiles.length > 0 && (
                <button
                  type="button"
                  onClick={handleInsuranceUpload}
                  disabled={insuranceUploading}
                  className="mt-3 w-full py-3 rounded-xl bg-primary-200 text-white font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {insuranceUploading
                    ? `Uploading... ${insuranceUploadProgress}%`
                    : `Upload ${pendingInsuranceFiles.length} file${
                        pendingInsuranceFiles.length > 1 ? "s" : ""
                      }`}
                </button>
              )}
            </div>
            )}

            <div className="mt-8 border border-neutral-200 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-text-color mb-5">
                Upload ID Documents
              </h2>

              <div
                {...getIdRootProps()}
                className={`flex flex-col items-center justify-center w-full border-2 border-dashed rounded-xl py-8 px-4 cursor-pointer transition-colors mb-4 ${
                  isIdDragActive
                    ? "border-primary-200 bg-blue-50"
                    : "border-neutral-200 hover:border-primary-200"
                }`}
              >
                <input {...getIdInputProps()} />
                <div className="w-12 h-12 rounded-full border-2 border-neutral-300 flex items-center justify-center mb-2">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
                      stroke="#9AA3AD"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <polyline
                      points="17 8 12 3 7 8"
                      stroke="#9AA3AD"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <line
                      x1="12"
                      y1="3"
                      x2="12"
                      y2="15"
                      stroke="#9AA3AD"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span className="text-sm font-medium text-text-color">
                  {isIdDragActive
                    ? "Drop files here…"
                    : "Drop or select file"}
                </span>
                <span className="text-xs text-neutral-400 mt-1">
                  Supported: Images, PDF. Max: 10MB
                </span>
              </div>

              {pendingIdFiles.map((file, i) => (
                <div
                  key={`pending-id-${i}`}
                  className="flex items-center gap-3 border border-neutral-200 rounded-lg px-4 py-3 mb-2"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-md flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
                        fill="#3182ce"
                        opacity="0.2"
                      />
                      <path
                        d="M14 2v6h6M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
                        stroke="#3182ce"
                        strokeWidth="1.5"
                        fill="none"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-color truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-neutral-400 mt-0.5">
                      {(file.size / (1024 * 1024)).toFixed(1)}MB · Ready to
                      upload
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setPendingIdFiles((prev) =>
                        prev.filter((_, idx) => idx !== i)
                      )
                    }
                    className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-neutral-100 hover:bg-red-50 text-neutral-400 hover:text-red-500 text-xs transition-colors"
                  >
                    &#x2715;
                  </button>
                </div>
              ))}

              {idDocuments.map((doc: any) => (
                <div
                  key={doc.id}
                  className="flex items-center gap-3 border border-neutral-200 rounded-lg px-4 py-3 mb-2"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-md flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
                        fill="#3182ce"
                        opacity="0.2"
                      />
                      <path
                        d="M14 2v6h6M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
                        stroke="#3182ce"
                        strokeWidth="1.5"
                        fill="none"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-color truncate">
                      {doc.name ?? doc.file_name ?? "ID Document"}
                    </p>
                    <p className="text-xs text-neutral-400 mt-0.5">
                      Uploaded Successfully
                    </p>
                  </div>
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-200 flex items-center justify-center">
                    <CheckIcon className="w-3.5 h-3.5 text-white" />
                  </div>
                </div>
              ))}

              {pendingIdFiles.length > 0 && (
                <button
                  type="button"
                  onClick={handleIdUpload}
                  disabled={idUploading}
                  className="mt-3 w-full py-3 rounded-xl bg-primary-200 text-white font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {idUploading
                    ? `Uploading... ${idUploadProgress}%`
                    : `Upload ${pendingIdFiles.length} file${
                        pendingIdFiles.length > 1 ? "s" : ""
                      }`}
                </button>
              )}
            </div>
          </>
        )}
        {bookingDetails?.is_long_term_rental && (
          <div className="mt-8">
            <ConnectedBookings bookingDetails={bookingDetails} />
          </div>
        )}
        <div className="mt-8">
          <PreviousBookings booking_id={bookingId ?? ""} />
        </div>
      </div>
    );
  };

  const renderCarDetailsSection = () => {
    return (
      <div>
        <div className="grid grid-cols-2 gap-y-8 gap-x-6 md:grid-cols-5">
          <div className="flex flex-col  ">
            <span className="text-md font-medium text-[#9AA3AD] my-2">
              Booking Status
            </span>
            <Badge
              className="items-center py-2 max-w-fit px-6"
              color={
                // 1NOW-1169: surface a pending modification/cancellation request
                // as the status (cancellation wins), shown like trip status
                // instead of a separate banner.
                bookingDetails?.cancellation_pending
                  ? "red"
                  : bookingDetails?.modification_pending
                  ? "yellow"
                  : bookingDetails?.trip_status === "pending"
                  ? "yellow"
                  : bookingDetails?.trip_status === "cancelled"
                  ? "red"
                  : "green"
              }
              name={
                bookingDetails?.cancellation_pending
                  ? "Cancellation Pending"
                  : bookingDetails?.modification_pending
                  ? "Modification Pending"
                  : bookingDetails?.trip_status
              }
            />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-md font-medium text-[#9AA3AD] my-2">
              Booking ID
            </span>
            <span className="text-base font-semibold text-[#1B2430]">
              # {bookingDetails?.id}
            </span>
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-md font-medium text-[#9AA3AD] my-2">
              Vehicle Name
            </span>
            <span className="text-base font-semibold truncate text-[#1B2430]">
              {bookingDetails?.fleet?.name}
            </span>
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-md font-medium text-[#9AA3AD] my-2">
              Renter Name
            </span>
            <span className="text-base font-semibold truncate text-[#1B2430]">
              {bookingDetails?.customer?.first_name}{" "}
              {bookingDetails?.customer?.last_name}
            </span>
          </div>
          {bookingDetails.drop_off_time &&
            bookingDetails.trip_status !== "cancelled" &&
            bookingDetails?.cancellation_pending !== true &&
            new Date(bookingDetails?.drop_off_time).getTime() +
              24 * 60 * 60 * 1000 >
              Date.now() && (
              <div className="col-span-2 lg:col-span-1 gap-2 flex items-center justify-center">
                {bookingDetails.trip_status !== "completed" && (
                  <button
                    className="w-auto px-4 py-2 text-sm font-medium text-black border border-[#263238] rounded-md hover:bg-gray-100 min-w-40 text-center whitespace-nowrap"
                    onClick={() => {
                      setIsCancelModalOpen(true);
                    }}
                  >
                    Cancel Booking
                  </button>
                )}
                <button
                  className="w-auto px-4 py-2 text-sm font-medium text-black border border-[#263238] rounded-md hover:bg-gray-100 min-w-40 text-center whitespace-nowrap"
                  onClick={() => {
                    setIsEdit(!isEdit);
                  }}
                >
                  {isEdit ? "Cancel Changes" : "Edit Booking"}
                </button>
              </div>
            )}
        </div>
      </div>
    );
  };

  const renderCancelBookingSection = () => {
    return (
      <div className="py-8 w-full">
        <h2 className="text-lg font-bold mb-8 text-text-color">Trip Info</h2>
        <div className="mb-4 flex justify-between items-center">
          <div>
            <p className="text-text-color">Total Bill</p>
            <p className="text-text-color">
              {bookingDetails?.invoice
                ?.total_price_after_coupon_without_security !== null
                ? bookingDetails?.invoice
                    ?.total_price_after_coupon_without_security
                : "$" + bookingDetails?.invoice?.total_price_without_security}
            </p>
          </div>

          <div
            onClick={handleOpenTotalBillModal}
            className="text-text-color cursor-pointer mx-2"
          >
            <div className="flex text-text-color font-semibold">
              View Invoice
              <svg
                width="23"
                height="26"
                viewBox="0 0 23 26"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                  fill="#90A3BF"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="mb-4 flex justify-between items-center">
          <div>
            <p className="text-text-color">Agreement</p>
          </div>
          <div
            onClick={openRentalAgreement}
            className="text-red-600 cursor-pointer mx-2"
          >
            <div className="flex text-red-600 font-semibold">
              View Agreement
              <svg
                width="23"
                height="26"
                viewBox="0 0 23 26"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                  fill="#90A3BF"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className="w-full flex flex-col space-y-8 ">
        {renderImgSection()}
        {renderCarDetailsSection()}

        <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
      </div>
    );
  };

  const rendernoBooking = () => {
    return (
      <div className="min-h-[60vh]">
        <p>Booking Not Found</p>
      </div>
    );
  };
  return (
    <>
      {bookingloading ? (
        <div className="flex justify-center items-center col-span-full py-96">
          <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
        </div>
      ) : (
        <div className={`nc-PayPage ${className}`} data-nc-id="PayPage">
          {/* 1NOW-1170: concrete pending-modification view — what changed
              (old → new), the price delta, and what will be charged on
              approval (from the backend `pending_modification` summary). */}
          {bookingDetails?.pending_modification && (
            <div className="container mt-4">
              <div className="max-w-6xl mx-auto rounded-md sm:rounded-xl border border-yellow-300 bg-yellow-50 p-3 sm:p-5">
                <div className="flex items-center gap-2">
                  <ExclamationTriangleIcon className="w-6 h-6 sm:w-7 sm:h-7 text-yellow-600 flex-shrink-0" />
                  <p className="font-bold text-yellow-900 text-sm sm:text-lg">
                    Modification pending — under review
                  </p>
                </div>
                {bookingDetails.pending_modification.approval_note && (
                  <p className="text-yellow-900 font-semibold text-xs sm:text-sm mt-1">
                    {bookingDetails.pending_modification.approval_note}
                  </p>
                )}
                <p className="text-yellow-800 text-xs sm:text-sm mt-1">
                  Your requested changes are awaiting approval. Here's what will
                  change and what you'll be charged once it's approved.
                </p>

                {bookingDetails.pending_modification.changes?.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {bookingDetails.pending_modification.changes.map(
                      (change: any, idx: number) => (
                        <div key={idx} className="text-xs sm:text-sm">
                          <span className="font-semibold text-gray-700">
                            {change.label}:{" "}
                          </span>
                          <span className="text-gray-600 line-through">
                            {change.old}
                          </span>
                          <span className="mx-1.5 text-gray-400">→</span>
                          <span className="text-gray-900 font-medium">
                            {change.new}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                )}

                {bookingDetails.pending_modification.price && (
                  <div className="mt-3 border-t border-yellow-200 pt-3 text-xs sm:text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Current total</span>
                      <span className="text-gray-900">
                        $
                        {formatPrice(
                          bookingDetails.pending_modification.price.old_total,
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">New total</span>
                      <span className="font-medium text-gray-900">
                        $
                        {formatPrice(
                          bookingDetails.pending_modification.price.new_total,
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold text-gray-900">
                      <span>
                        {bookingDetails.pending_modification.price.difference < 0
                          ? "Refund"
                          : "Additional charge"}
                      </span>
                      <span>
                        $
                        {formatPrice(
                          Math.abs(
                            bookingDetails.pending_modification.price.difference,
                          ),
                        )}
                      </span>
                    </div>
                  </div>
                )}

                {bookingDetails.pending_modification.charge?.amount > 0 && (
                  <p className="mt-3 text-xs sm:text-sm text-yellow-900">
                    {bookingDetails.pending_modification.charge.direction ===
                    "refund"
                      ? "A refund of "
                      : "A charge of "}
                    <span className="font-semibold">
                      $
                      {formatPrice(
                        bookingDetails.pending_modification.charge.amount,
                      )}
                    </span>
                    {bookingDetails.pending_modification.charge.card_last_four
                      ? ` to your card ···· ${bookingDetails.pending_modification.charge.card_last_four}`
                      : ""}{" "}
                    will be applied{" "}
                    {bookingDetails.pending_modification.charge.when}.
                  </p>
                )}
              </div>
            </div>
          )}

          {!identityLink &&
            bookingDetails?.trip_status !== "cancelled" &&
            bookingDetails?.cancellation_pending !== true &&
            bookingDetails &&
            bookingDetails?.customer?.identity_verified !== true && (
              <>
                <div className="container mt-4">
                  <div className="max-w-6xl mx-auto rounded-xl bg-[#FF757587] min-h-22 flex flex-row items-center justify-between p-4">
                    <span className="text-[#FF0000] font-medium text-[14px] md:text-2xl">
                      Verify your{" "}
                      <span className="font-extrabold">identity</span> to
                      confirm your booking
                    </span>
                    <ButtonPrimary
                      onClick={handlePrimaryDriverVerificationButtonClick}
                      className="!bg-[#FF0000] min-w-44 !hover:bg-[#FF0000] text-white text-[12px] font-bold py-2 px-2 rounded-xl whitespace-nowrap"
                    >
                      {identityLoading ? (
                        <div className="flex justify-center items-center col-span-full">
                          <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
                        </div>
                      ) : (
                        <>
                          <span className="block sm:hidden">Create Link</span>
                          <span className="hidden sm:block">
                            Create Verification Link
                          </span>
                        </>
                      )}
                    </ButtonPrimary>
                  </div>
                </div>
              </>
            )}
          {identityLink && (
            <div className="container mt-4">
              <div className="max-w-6xl mx-auto rounded-xl bg-[#FF757587] min-h-22 flex flex-row items-center justify-between p-4">
                <span className="text-white font-medium text-[14px] md:text-2xl break-words truncate">
                  {identityLink}
                </span>
                <div className="space-x-0 md:space-x-4 flex flex-col md:flex-row space-y-4 md:space-y-0">
                  {/* <button className="ttnc-ButtonPrimary disabled:bg-opacity-70 bg-none border text-sm border-[#FF0000] text-[#FF0000] font-bold py-3 px-4 sm:px-6 rounded-xl">
                    Purchase Insurance
                  </button> */}
                  <ButtonPrimary
                    onClick={() => {
                      navigator.clipboard.writeText(identityLink);
                      toast.success("Link Copied", { id: COPY_TOAST_ID });
                    }}
                    className="!bg-[#FF0000] min-w-20 md:ml-2 !hover:bg-[#FF0000] text-white text-[12px] font-bold rounded-xl px-4 py-3 whitespace-nowrap"
                  >
                    Copy
                  </ButtonPrimary>
                  <ButtonPrimary
                    onClick={() => window.open(identityLink, "_blank")}
                    className="!bg-[#FF0000] min-w-20 md:ml-2 !hover:bg-[#FF0000] text-white text-[12px] font-bold rounded-xl px-4 py-3 whitespace-nowrap"
                  >
                    Open
                  </ButtonPrimary>
                </div>
              </div>
            </div>
          )}
          {!canopyLink &&
            bookingDetails?.trip_status !== "cancelled" &&
            bookingDetails?.cancellation_pending !== true &&
            bookingDetails &&
            bookingDetails?.customer?.insurance_verified !== true && (
              <div className="container mt-4">
                <div className="max-w-6xl mx-auto rounded-xl bg-[#FF757587] min-h-22 flex flex-row items-center justify-between p-4">
                  <span className="text-[#FF0000] font-medium text-[14px] md:text-2xl ">
                    Verify your{" "}
                    <span className="font-extrabold">insurance</span> to confirm
                    your booking
                  </span>
                  <div className="space-x-4 flex-col md:flex-row space-y-4">
                    <ButtonPrimary
                      onClick={handleCanopyVerificationButtonClick}
                      className="!bg-[#FF0000] min-w-44 !hover:bg-[#FF0000] text-white text-[12px] font-bold rounded-xl px-4 py-3 whitespace-nowrap"
                    >
                      {canopyLoading ? (
                        <div className="flex justify-center items-center col-span-full">
                          <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
                        </div>
                      ) : (
                        <>
                          <span className="block sm:hidden">Create Link</span>
                          <span className="hidden sm:block">
                            Create Verification Link
                          </span>
                        </>
                      )}
                    </ButtonPrimary>
                  </div>
                </div>
              </div>
            )}
          {canopyLink && (
            <div className="container mt-4">
              <div className="max-w-6xl mx-auto rounded-xl bg-[#FF757587] min-h-22 flex flex-row items-center justify-between p-4">
                <span className="text-white font-medium text-[14px] md:text-2xl break-words truncate">
                  {canopyLink}
                </span>
                <div className="space-x-0 md:space-x-4 flex flex-col md:flex-row space-y-4 md:space-y-0">
                  {/* <button className="ttnc-ButtonPrimary disabled:bg-opacity-70 bg-none border text-sm border-[#FF0000] text-[#FF0000] font-bold py-3 px-4 sm:px-6 rounded-xl">
                    Purchase Insurance
                  </button> */}
                  <ButtonPrimary
                    onClick={() => {
                      navigator.clipboard.writeText(canopyLink);
                      toast.success("Link Copied", { id: COPY_TOAST_ID });
                    }}
                    className="!bg-[#FF0000] min-w-20 md:ml-2 !hover:bg-[#FF0000] text-white text-[12px] font-bold rounded-xl px-4 py-3 whitespace-nowrap"
                  >
                    Copy
                  </ButtonPrimary>
                  <ButtonPrimary
                    onClick={() => window.open(canopyLink, "_blank")}
                    className="!bg-[#FF0000] min-w-20 md:ml-2 !hover:bg-[#FF0000] text-white text-[12px] font-bold rounded-xl px-4 py-3 whitespace-nowrap"
                  >
                    Open
                  </ButtonPrimary>
                </div>
              </div>
            </div>
          )}
          {!driveridentityLink &&
            bookingDetails?.trip_status !== "cancelled" &&
            bookingDetails?.cancellation_pending !== true &&
            bookingDetails &&
            bookingDetails?.secondary_driver_obj?.identity_verified !== null &&
            bookingDetails?.secondary_driver_obj?.identity_verified ===
              false && (
              <>
                <div className="container mt-4">
                  <div className="max-w-6xl mx-auto rounded-xl bg-[#FF757587] min-h-22 flex flex-row items-center justify-between p-4">
                    <span className="text-[#FF0000] font-medium text-[14px] md:text-2xl">
                      Verify your{" "}
                      <span className="font-extrabold">
                        {" "}
                        secondary driver identity
                      </span>{" "}
                      to confirm your booking
                    </span>
                    <ButtonPrimary
                      onClick={handleDriverPrimaryDriverVerificationButtonClick}
                      className="!bg-[#FF0000] min-w-44 !hover:bg-[#FF0000] text-white text-[12px] font-bold py-2 px-2 rounded-xl whitespace-nowrap"
                    >
                      {driveridentityLoading ? (
                        <div className="flex justify-center items-center col-span-full">
                          <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
                        </div>
                      ) : (
                        <>
                          <span className="block sm:hidden">Create Link</span>
                          <span className="hidden sm:block">
                            Create Verification Link
                          </span>
                        </>
                      )}
                    </ButtonPrimary>
                  </div>
                </div>
              </>
            )}
          {driveridentityLink && (
            <div className="container mt-4">
              <div className="max-w-6xl mx-auto rounded-xl bg-[#FF757587] min-h-22 flex flex-row items-center justify-between p-4">
                <span className="text-white font-medium text-[14px] md:text-2xl break-words truncate">
                  {driveridentityLink}
                </span>
                <div className="space-x-0 md:space-x-4 flex flex-col md:flex-row space-y-4 md:space-y-0">
                  {/* <button className="ttnc-ButtonPrimary disabled:bg-opacity-70 bg-none border text-sm border-[#FF0000] text-[#FF0000] font-bold py-3 px-4 sm:px-6 rounded-xl">
                    Purchase Insurance
                  </button> */}
                  <ButtonPrimary
                    onClick={() => {
                      navigator.clipboard.writeText(driveridentityLink);
                      toast.success("Link Copied", { id: COPY_TOAST_ID });
                    }}
                    className="!bg-[#FF0000] min-w-20 md:ml-2 !hover:bg-[#FF0000] text-white text-[12px] font-bold rounded-xl px-4 py-3 whitespace-nowrap"
                  >
                    Copy
                  </ButtonPrimary>
                  <ButtonPrimary
                    onClick={() => window.open(driveridentityLink, "_blank")}
                    className="!bg-[#FF0000] min-w-20 md:ml-2 !hover:bg-[#FF0000] text-white text-[12px] font-bold rounded-xl px-4 py-3 whitespace-nowrap"
                  >
                    Open
                  </ButtonPrimary>
                </div>
              </div>
            </div>
          )}
          {!drivercanopyLink &&
            bookingDetails?.trip_status !== "cancelled" &&
            bookingDetails?.cancellation_pending !== true &&
            bookingDetails &&
            bookingDetails?.secondary_driver_obj !== null &&
            bookingDetails?.secondary_driver_obj?.insurance_verified ===
              false && (
              <div className="container mt-4">
                <div className="max-w-6xl mx-auto rounded-xl bg-[#FF757587] min-h-22 flex flex-row items-center justify-between p-4">
                  <span className="text-[#FF0000] font-medium text-[14px] md:text-2xl ">
                    Verify your{" "}
                    <span className="font-extrabold">
                      secondary driver insurance
                    </span>{" "}
                    to confirm your booking
                  </span>
                  <div className="space-x-4 flex-col md:flex-row space-y-4">
                    <ButtonPrimary
                      onClick={handleDriverCanopyVerificationButtonClick}
                      className="!bg-[#FF0000] min-w-44 !hover:bg-[#FF0000] text-white text-[12px] font-bold rounded-xl px-4 py-3 whitespace-nowrap"
                    >
                      {drivercanopyLoading ? (
                        <div className="flex justify-center items-center col-span-full">
                          <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
                        </div>
                      ) : (
                        <>
                          <span className="block sm:hidden">Create Link</span>
                          <span className="hidden sm:block">
                            Create Verification Link
                          </span>
                        </>
                      )}
                    </ButtonPrimary>
                  </div>
                </div>
              </div>
            )}
          {drivercanopyLink && (
            <div className="container mt-4">
              <div className="max-w-6xl mx-auto rounded-xl bg-[#FF757587] min-h-22 flex flex-row items-center justify-between p-4">
                <span className="text-white font-medium text-[14px] md:text-2xl break-words truncate">
                  {drivercanopyLink}
                </span>
                <div className="space-x-0 md:space-x-4 flex flex-col md:flex-row space-y-4 md:space-y-0">
                  {/* <button className="ttnc-ButtonPrimary disabled:bg-opacity-70 bg-none border text-sm border-[#FF0000] text-[#FF0000] font-bold py-3 px-4 sm:px-6 rounded-xl">
                    Purchase Insurance
                  </button> */}
                  <ButtonPrimary
                    onClick={() => {
                      navigator.clipboard.writeText(drivercanopyLink);
                      toast.success("Link Copied", { id: COPY_TOAST_ID });
                    }}
                    className="!bg-[#FF0000] min-w-20 md:ml-2 !hover:bg-[#FF0000] text-white text-[12px] font-bold rounded-xl px-4 py-3 whitespace-nowrap"
                  >
                    Copy
                  </ButtonPrimary>
                  <ButtonPrimary
                    onClick={() => window.open(drivercanopyLink, "_blank")}
                    className="!bg-[#FF0000] min-w-20 md:ml-2 !hover:bg-[#FF0000] text-white text-[12px] font-bold rounded-xl px-4 py-3 whitespace-nowrap"
                  >
                    Open
                  </ButtonPrimary>
                </div>
              </div>
            </div>
          )}
          <Transition appear show={isCancellationOpen} as={Fragment}>
            <Dialog
              as="div"
              className="HeroSearchFormMobile__Dialog relative z-50"
              onClose={() => {
                setIsCancellationOpen(false);
              }}
              static={true}
            >
              <div className="fixed inset-0 bg-neutral-100 dark:bg-[#333333]">
                <div className="flex h-full">
                  <TransitionChild
                    as={Fragment}
                    enter="ease-out transition-transform"
                    enterFrom="opacity-0 translate-y-52"
                    enterTo="opacity-100 translate-y-0"
                    leave="ease-in transition-transform"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-52"
                  >
                    <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between ">
                      <>
                        <div className="absolute left-4 top-4 z-0">
                          <button
                            className="focus:outline-none focus:ring-0"
                            onClick={() => {
                              setIsCancellationOpen(false);
                              setIsCancelModalOpen(true);
                            }}
                          >
                            <XMarkIcon className="w-5 h-5 text-black dark:text-white" />
                          </button>
                        </div>

                        <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                          <div className="flex-1 flex flex-col bg-white dark:bg-[#333333] z-10">
                            <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                              <Cancellation terms={cancellationPolicy} />
                            </div>
                          </div>
                        </div>
                        <div className="justify-end px-4 py-3 bg-white dark:bg-[#333333] border-t border-neutral-200 dark:border-[#FFFFFF33] flex">
                          <ButtonPrimary
                            sizeClass="px-6 py-3 !rounded-lg"
                            onClick={() => {
                              setIsCancellationOpen(false);
                              setCancellationPolicyChecked(true);
                              setIsCancelModalOpen(true);
                            }}
                          >
                            Accept
                          </ButtonPrimary>
                        </div>
                      </>
                    </DialogPanel>
                  </TransitionChild>
                </div>
              </div>
            </Dialog>
          </Transition>
          <Transition appear show={isCancelModalOpen}>
            <Dialog as="div" className="relative z-50" onClose={() => {}}>
              <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
              <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                  <TransitionChild
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 transform-[scale(95%)]"
                    enterTo="opacity-100 transform-[scale(100%)]"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 transform-[scale(100%)]"
                    leaveTo="opacity-0 transform-[scale(95%)]"
                  >
                    <DialogPanel className="w-full max-w-md bg-white dark:bg-[#111111] rounded-xl p-6 shadow-2xl">
                      <div className="flex justify-between items-center mb-4">
                        <DialogTitle
                          as="h3"
                          className="text-xl font-bold text-text-color"
                        >
                          Cancel Booking
                        </DialogTitle>
                        <button
                          onClick={() => setIsCancelModalOpen(false)}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>

                      <p className="text-gray-600 dark:text-gray-400 mb-6">
                        Are you sure you want to cancel your booking? This action
                        cannot be undone.
                      </p>

                      <div className="flex gap-2 mt-4 p-4 border border-neutral-200 dark:border-[#FFFFFF33] rounded-md overflow-hidden z-10">
                        <Checkbox
                          checked={cancellationPolicyChecked}
                          onChange={(checked) => {
                            if (checked) {
                              setIsCancellationOpen(true);
                              setIsCancelModalOpen(false);
                            } else {
                              setCancellationPolicyChecked(false);
                              setIsCancelModalOpen(true);
                            }
                          }}
                          className="group size-7 rounded-md p-1 ring-1 ring-primary-200 data-[checked]:bg-primary-200 data-[checked]:bg-opacity-20 ring-inset  mr-4"
                        >
                          <CheckIcon
                            className={`size-5 stroke-white ${
                              cancellationPolicyChecked
                                ? "visible"
                                : "invisible"
                            }`}
                          />
                        </Checkbox>
                        <div>
                          <div className="text-gray-400 text-lg font-medium">
                            I hereby confirm that I have read, understood, and
                            accept the{" "}
                            <span
                              className="text-text-color text-xl font-bold cursor-pointer underline"
                              onClick={() => {
                                setIsCancellationOpen(true);
                                setIsCancelModalOpen(false);
                              }}
                            >
                              Cancellation Policy.
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-3 justify-end mt-6">
                        <ButtonPrimary
                          onClick={() => setIsCancelModalOpen(false)}
                          disabled={isCancelling}
                          className="!border !bg-transparent !rounded-lg hover:!bg-white text-text-color"
                        >
                          No
                        </ButtonPrimary>
                        <ButtonPrimary
                          onClick={handleCancelBooking}
                          className="!border-none !rounded-lg"
                        >
                          {isCancelling ? (
                            <div className="flex items-center gap-2">
                              <div className="spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full border-t-transparent border-white"></div>
                            </div>
                          ) : (
                            "Yes"
                          )}
                        </ButtonPrimary>
                      </div>
                    </DialogPanel>
                  </TransitionChild>
                </div>
              </div>
            </Dialog>
          </Transition>
          <Transition appear show={isOpen}>
            <Dialog
              as="div"
              className="relative z-10 focus:outline-none border"
              onClose={close}
            >
              <div className="fixed inset-0 z-10 w-screen overflow-y-auto border border-primary-6000">
                <div className="flex min-h-full items-center justify-center p-4">
                  <TransitionChild
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 transform-[scale(95%)]"
                    enterTo="opacity-100 transform-[scale(100%)]"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 transform-[scale(100%)]"
                    leaveTo="opacity-0 transform-[scale(95%)]"
                  >
                    <DialogPanel className="w-full max-w-md bg-white rounded-x p-6 backdrop-blur-2xl rounded-xl  border border-primary-6000">
                      <div className="flex justify-between mb-6">
                        <DialogTitle as="h3" className="font-medium text-xl">
                          Modify Trip
                        </DialogTitle>
                        <button onClick={close}>
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>

                      <ButtonPrimary
                        className="bg-primary-500 w-full mb-5"
                        onClick={() => {
                          modifyBooking(bookingId!);
                        }}
                      >
                        Modify Booking
                      </ButtonPrimary>
                    </DialogPanel>
                  </TransitionChild>
                </div>
              </div>
            </Dialog>
          </Transition>
          <Transition appear show={isTotalBillModalOpen}>
            <Dialog
              as="div"
              className="relative z-50"
              onClose={handleCloseTotalBillModal}
            >
              <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                  <TransitionChild
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 transform scale-95"
                    enterTo="opacity-100 transform scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 transform scale-100"
                    leaveTo="opacity-0 transform scale-95"
                  >
                    <DialogPanel className="w-full max-w-md bg-bg-color border border-neutral-200 p-6 rounded-md shadow-lg !z-50">
                      <div className="flex justify-between mb-4 border-b border-neutral-200 pb-2">
                        <DialogTitle as="h2" className="text-xl font-bold">
                          Invoice
                        </DialogTitle>
                        <button onClick={handleCloseTotalBillModal}>
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>
                      <Invoice
                        invoice={bookingDetails?.invoice}
                        carName={bookingDetails?.fleet?.name}
                      />
                    </DialogPanel>
                  </TransitionChild>
                </div>
              </div>
            </Dialog>
          </Transition>

          {bookingDetails && (
            <TripInspectionModal
              open={isInspectionOpen}
              onClose={() => setIsInspectionOpen(false)}
              bookingId={bookingUid}
              bookingLabel={{
                reference: bookingDetails?.id,
                vehicle: bookingDetails?.fleet?.name,
                plate: bookingDetails?.fleet?.plate_number,
              }}
              initialPhase={inspectionPhase}
              preOdometerImage={preOdometerImage}
              postOdometerImage={postOdometerImage}
              preOdometerLoading={preOdometerLoading}
              postOdometerLoading={postOdometerLoading}
              preOdometerResult={preOdometerResult}
              postOdometerResult={postOdometerResult}
              preOdometerError={preOdometerError}
              postOdometerError={postOdometerError}
              onUploadOdometer={handleUploadOdometer}
            />
          )}
          <main className="container mt-11 mb-24 lg:mb-32 ">
            <div className="max-w-6xl mx-auto">
              {bookingloading ? (
                <div className="flex justify-center items-center col-span-full py-96">
                  <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
                </div>
              ) : bookingDetails !== null ? (
                <>
                  {renderContent()}
                  {isEdit && (
                    <EditBooking
                      startTime={startTime}
                      setStartTime={setStartTime}
                      endTime={endTime}
                      setEndTime={setEndTime}
                      bookingDetails={bookingDetails}
                      fetchBookingDetail={fetchBookingDetail}
                      onClose={() => setIsEdit(false)}
                    />
                  )}
                  {bookingDetails?.trip_status !== "cancelled" &&
                  bookingDetails?.cancellation_pending !== true
                    ? renderBookingDetailsSection()
                    : renderCancelBookingSection()}
                </>
              ) : (
                rendernoBooking()
              )}
            </div>
          </main>
        </div>
      )}
    </>
  );
};

export default BookingDetailPage;
